import { useState } from "react";
import { motion } from "framer-motion";
import { Card, CardContent } from "./components/ui/card";
import {
  Server,
  ShieldCheck,
  Cloud,
  Boxes,
  Network,
  Lock,
  Mail,
  Linkedin,
  RefreshCw,
  Users,
  Book,
  Paperclip,
  Menu,
  X,
} from "lucide-react";
import { Link } from "react-scroll";

export default function Portfolio() {
  const [isOpen, setIsOpen] = useState(false);

  const projects = [
    {
      title: "Networking",
      description: "Designed and maintained robust network infrastructures",
      icon: <Network className="w-8 h-8 text-blue-400" />,
    },
    {
      title: "Firewall",
      description:
        "Configured and secured networks with OPNsense and FortiGate firewalls.",
      icon: <ShieldCheck className="w-8 h-8 text-green-400" />,
    },
    {
      title: "Security with Wazuh",
      description: "Implemented Wazuh for monitoring and intrusion detection.",
      icon: <Lock className="w-8 h-8 text-red-400" />,
    },
    {
      title: "Docker & Containers",
      description: "Running multiple services in isolated Docker containers.",
      icon: <Boxes className="w-8 h-8 text-yellow-400" />,
    },
    {
      title: "Documentation",
      description:
        "Self-hosted Bookstack for organizing my homelab documentation and supporting knowledge management at work.",
      icon: <Server className="w-8 h-8 text-purple-400" />,
    },
    {
      title: "Storage",
      description: "Private cloud storage for files and photos with Nextcloud in combination with Truenas.",
      icon: <Cloud className="w-8 h-8 text-cyan-400" />,
    },
  ];

  const soon = [
    {
      title: "Migration to Proxmox",
      description:
        "Planned migration from VMware ESXi to Proxmox for virtualization.",
      icon: <RefreshCw className="w-8 h-8 text-orange-400 animate-spin-slow" />,
    },
    {
      title: "Kubernetes",
      description:
        "Planned switch from docker to kubernetes.",
      icon: <RefreshCw className="w-8 h-8 text-orange-400 animate-spin-slow" />,
    },
  ];

  const skills = [
    "Docker",
    "Linux",
    "Networking",
    "Firewalls",
    "Automation",
    "Monitoring",
    "Cloud",
    "Virtualization",
  ];

  const employment = [
    {
      area: "Cloud",
      description:
        "Managing users and devices in Azure, creating enterprise applications, Intune policies, and handling Exchange and Entra configurations.",
      icon: <Cloud className="w-8 h-8 text-blue-400 mx-auto" />,
    },
    {
      area: "On-Prem",
      description:
        "Maintaining and managing on-prem servers and remote environments, deploying updates, handling backups, and ensuring reliable operation of internal systems.",
      icon: <Server className="w-8 h-8 text-purple-400 mx-auto" />,
    },
    {
      area: "Networking",
      description:
        "Handling escalated network issues and complex tickets, working closely with internal teams and vendors to deliver timely and secure solutions.",
      icon: <Network className="w-8 h-8 text-green-400 mx-auto" />,
    },
    {
      area: "Workplace",
      description:
        "Providing L1 and L2 support for over 100 end-users, managing diverse technical issues with full administrative access across multiple infrastructures.",
      icon: <Users className="w-8 h-8 text-yellow-400 mx-auto" />,
    },
    {
      area: "Documentation",
      description:
        "Maintaining and updating internal documentation, including homelab setups and work procedures, ensuring knowledge is organized and accessible.",
      icon: <Book className="w-8 h-8 text-red-400 mx-auto" />,
    },
  ];

  const studies = [
    {
      title:
        "Graduate in System and Network administration (Highest honor)",
      institution: "Artesis Plantijn Hogeschool",
      year: "2022 - 2024",
      icon: <Book className="w-8 h-8 text-purple-400 mx-auto" />,
    },
    {
      title: "CCNA",
      institution: "Netacad",
      year: "In Progress",
      icon: <Paperclip className="w-8 h-8 text-blue-400 mx-auto" />,
    },
    {
      title: "A2 in IT Administration",
      institution: "Sint-Lodewijk",
      year: "2016",
      icon: <Book className="w-8 h-8 text-purple-400 mx-auto" />,
    },
    {
      title: "Client communication",
      institution: "Jan Roel - SB Management",
      year: "2025",
      icon: <Book className="w-8 h-8 text-purple-400 mx-auto" />,
    },
  ];

  const navLinks = [
    { to: "top", label: "Home" },
    { to: "experience", label: "Experience" },
    { to: "studies", label: "Studies" },
    { to: "demo", label: "Demo" },
    { to: "contact", label: "Contact" },
  ];

  return (
    <div id="top" className="min-h-screen bg-gray-900 text-gray-100">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-gray-800 shadow-md">
        <nav className="max-w-6xl mx-auto flex justify-between items-center h-16 px-6">
          {/* Logo */}
          <div className="flex items-center space-x-2">
            <img
              src="/logo4.png" 
              alt="Logo" 
              className="h-14 w-14 rounded-full object-cover"
            />
             <span className="text-xl font-bold text-white">
              Atlas-Networks
            </span>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex space-x-6">
            {navLinks.map((item, i) => (
              <Link
                key={i}
                to={item.to}
                smooth={true}
                duration={600}
                offset={-80}
                className="text-white hover:text-blue-400 transition cursor-pointer"
              >
                {item.label}
              </Link>
            ))}
          </div>

          {/* Mobile Hamburger */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-white focus:outline-none"
            >
              {isOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </nav>

        {/* Mobile Dropdown */}
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="md:hidden bg-gray-700 px-6 py-4 space-y-4"
          >
            {navLinks.map((item, i) => (
              <Link
                key={i}
                to={item.to}
                smooth={true}
                duration={600}
                offset={-80}
                onClick={() => setIsOpen(false)}
                className="block text-white hover:text-blue-400 transition cursor-pointer"
              >
                {item.label}
              </Link>
            ))}
          </motion.div>
        )}
      </header>

      {/* Hero Section */}
      <section className="max-w-6xl mx-auto px-6 py-16 text-center">
        <motion.h2
          className="text-4xl font-bold mb-4"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          Hey there! Welcome!
        </motion.h2>
        <p className="text-lg text-gray-300 max-w-2xl mx-auto">
          I’m a systems enthusiast passionate about networking and security. My
          homelab is my playground for learning real-world IT skills and solving
          complex problems besides my job.
        </p>
      </section>

      {/* Skills Section */}
      <section className="max-w-6xl mx-auto px-6 pb-16">
        <h2 className="text-3xl font-bold text-center mb-8">Skills</h2>
        <div className="flex flex-wrap justify-center gap-3">
          {skills.map((skill, i) => (
            <span
              key={i}
              className="px-4 py-2 bg-gray-800 rounded-full shadow text-gray-200 hover:bg-gray-700"
            >
              {skill}
            </span>
          ))}
        </div>
      </section>

      {/* Projects Grid */}
      <section className="max-w-6xl mx-auto px-6 pb-16">
        <h2 className="text-3xl font-bold text-center mb-8">Projects</h2>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {projects.map((proj, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              viewport={{ once: true }}
            >
              <Card className="rounded-2xl shadow hover:shadow-lg transition bg-gray-800 border border-gray-700">
                <CardContent className="p-6 flex flex-col items-center text-center">
                  {proj.icon}
                  <h3 className="mt-4 font-semibold text-xl text-white">
                    {proj.title}
                  </h3>
                  <p className="mt-2 text-gray-300">{proj.description}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Coming Soon */}
      <section className="max-w-6xl mx-auto px-6 pb-16">
        <h2 className="text-3xl font-bold text-center mb-8">Coming Soon</h2>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {soon.map((proj, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              viewport={{ once: true }}
            >
              <Card className="rounded-2xl shadow hover:shadow-lg transition bg-gray-800 border border-gray-700">
                <CardContent className="p-6 flex flex-col items-center text-center">
                  {proj.icon}
                  <h3 className="mt-4 font-semibold text-xl text-white">
                    {proj.title}
                  </h3>
                  <p className="mt-2 text-gray-300">{proj.description}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Employment Section */}
      <section id="experience" className="bg-gray-800 py-16 scroll-mt-20">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-8">Experience</h2>
          <p className="text-center text-gray-300 mb-12">
            A summary of my hands-on experience with IT systems, networking,
            cloud platforms, and documentation.
          </p>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {employment.map((role, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="rounded-2xl shadow hover:shadow-lg transition bg-gray-900 border border-gray-700">
                  <CardContent className="p-6 text-center">
                    {role.icon}
                    <h3 className="mt-4 font-semibold text-xl text-white">
                      {role.area}
                    </h3>
                    <p className="mt-2 text-gray-300">{role.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Studies Section */}
      <section id="studies" className="max-w-6xl mx-auto px-6 pt-24 pb-16 scroll-mt-20">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-8">
            Studies & Certifications
          </h2>
          <p className="text-center text-gray-300 mb-12">
            My educational background and professional certifications.
          </p>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {studies.map((study, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="rounded-2xl shadow hover:shadow-lg transition bg-gray-900 border border-gray-700">
                  <CardContent className="p-6 text-center">
                    {study.icon}
                    <h3 className="mt-4 font-semibold text-xl text-white">
                      {study.title}
                    </h3>
                    <p className="mt-2 text-gray-300">
                      {study.institution} | {study.year}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Demo Section */}
<section id="demo" className="bg-gray-800 py-20 scroll-mt-20">
  <div className="max-w-3xl mx-auto px-6 text-center">
    <h2 className="text-3xl font-bold mb-6">Demo</h2>
    
    <div className="bg-gray-900 border border-gray-700 rounded-2xl shadow-lg p-8">
      <p className="text-gray-300 text-lg mb-6">
        Access my <span className="font-semibold text-white"> homelab dashboard </span> 
        to see live services and experiments.  
      </p>
      <p className="text-gray-400 italic mb-8">
        ⚠️ For security reasons this can only be shown during an interview.
      </p>
      <a href="http://unlockduringinterview" target="_blank"className="px-6 py-3 bg-purple-600 hover:bg-purple-500 text-white font-medium rounded-lg shadow transition">
        Visit Dashboard
      </a>
    </div>
  </div>
</section>

      {/* Contact Section */}
      <section id="contact" className="bg-gray-800 py-16 scroll-mt-20">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6">Get in Touch</h2>
          <p className="text-gray-300 mb-8">
            I’d love to connect! Feel free to reach out if you want to chat
            about potential opportunities.
          </p>
          <div className="flex justify-center gap-6">
            <a
              href="mailto:lotfi.elouaamari@outlook.com"
              className="flex items-center gap-2 text-gray-300 hover:text-blue-400"
            >
              <Mail />
              lotfi.elouaamari@outlook.com
            </a>
            <a href="https://www.linkedin.com/in/lotfi-el-ouaamari-5a57281bb/" target="_blank" className="flex items-center gap-2 text-gray-300 hover:text-blue-400">
              <Linkedin /> LinkedIn
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 p-6 text-center text-gray-500 border-t border-gray-800">
        © {new Date().getFullYear()} Atlas-Networks. Built with ❤️
      </footer>
    </div>
  );
}